import React from "react";
import { AlertTriangle, Inbox, RotateCw, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { RUNBOOK } from "@/constants/testIds";

export function LoadingCards({ count = 6 }) {
  return (
    <div data-testid={RUNBOOK.loading} className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-xl border border-border bg-card p-5 space-y-4">
          <div className="flex justify-between">
            <Skeleton className="h-5 w-20" />
            <Skeleton className="h-5 w-14" />
          </div>
          <Skeleton className="h-6 w-4/5" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
          <div className="flex gap-2 pt-2">
            <Skeleton className="h-6 w-16" />
            <Skeleton className="h-6 w-16" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ErrorState({ message, onRetry }) {
  return (
    <div
      data-testid={RUNBOOK.errorState}
      className="tr-fade-up rounded-xl border border-red-500/30 bg-red-500/5 p-10 flex flex-col items-center text-center gap-4"
    >
      <div className="h-14 w-14 rounded-full bg-red-500/10 flex items-center justify-center">
        <AlertTriangle className="h-7 w-7 text-red-500" />
      </div>
      <div>
        <h3 className="text-lg font-semibold">Couldn't load runbooks</h3>
        <p className="text-sm text-muted-foreground mt-1 max-w-md">{message}</p>
      </div>
      {onRetry && (
        <Button data-testid={RUNBOOK.retryBtn} onClick={onRetry} variant="outline" className="gap-2">
          <RotateCw className="h-4 w-4" /> Retry
        </Button>
      )}
    </div>
  );
}

export function EmptyState({ hasFilters, query, onReset, onCreate }) {
  return (
    <div
      data-testid={RUNBOOK.emptyState}
      className="tr-fade-up rounded-xl border border-dashed border-border bg-card/40 p-12 flex flex-col items-center text-center gap-4"
    >
      <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center">
        {hasFilters ? <Search className="h-8 w-8 text-primary" /> : <Inbox className="h-8 w-8 text-primary" />}
      </div>
      {hasFilters ? (
        <>
          <div>
            <h3 className="text-lg font-semibold">No runbooks match your search</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {query ? (
                <>
                  Nothing found for <span className="font-mono text-foreground">"{query}"</span>.
                </>
              ) : (
                "No runbooks match the selected filters."
              )}
            </p>
          </div>
          <Button data-testid={RUNBOOK.resetFiltersBtn} onClick={onReset} variant="outline">
            Reset filters
          </Button>
        </>
      ) : (
        <>
          <div>
            <h3 className="text-lg font-semibold">No runbooks yet</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-sm">
              Be the first to document an operational procedure your team can follow. Sign in with Google to create one.
            </p>
          </div>
          {onCreate && (
            <Button data-testid="empty-create-runbook-btn" onClick={onCreate}>
              Create runbook
            </Button>
          )}
        </>
      )}
    </div>
  );
}
